import { AJUDA_PRODUCAO } from "@/lib/ajuda/dialogos/producao";
import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@bookdental/ui";
import {
  SORT_FIELDS,
  SORT_FIELD_LABELS,
  SORT_LEVEL_LABELS,
  type SortDirection,
  type SortField,
} from "../production-sort";

type Props = {
  aberto: boolean;
  criterios: SortField[];
  direcoes: SortDirection[];
  onFechar: () => void;
  onAplicar: (criterios: SortField[], direcoes: SortDirection[]) => void;
};

/**
 * Ordenação da Planilha no celular — as MESMAS três prioridades da barra do desktop, só que em
 * folha inferior. Edita numa cópia e só aplica no fim: a lista está coberta pela folha, e
 * reordenar a cada toque mudaria algo que o usuário não está vendo.
 */
export function MobileSortSheet({ aberto, criterios, direcoes, onFechar, onAplicar }: Props) {
  const [rascunhoCriterios, setRascunhoCriterios] = useState<SortField[]>(criterios);
  const [rascunhoDirecoes, setRascunhoDirecoes] = useState<SortDirection[]>(direcoes);

  useEffect(() => {
    if (aberto) {
      setRascunhoCriterios(criterios);
      setRascunhoDirecoes(direcoes);
    }
  }, [aberto, criterios, direcoes]);

  const trocarCampo = (nivel: number, campo: SortField) =>
    setRascunhoCriterios((prev) => prev.map((c, i) => (i === nivel ? campo : c)));

  const alternarDirecao = (nivel: number) =>
    setRascunhoDirecoes((prev) => prev.map((d, i) => (i === nivel ? (d === "asc" ? "desc" : "asc") : d)));

  return (
    <Sheet open={aberto} onOpenChange={(v) => !v && onFechar()}>
      <SheetContent
        side="bottom"
        className="flex max-h-[85dvh] flex-col gap-0 rounded-t-2xl border-zinc-200 bg-white p-0 dark:border-border dark:bg-card"
      >
        <SheetHeader className="border-b border-zinc-100 px-4 py-3 text-left dark:border-border">
          <SheetTitle className="text-sm font-bold text-zinc-900 dark:text-foreground" ajuda={AJUDA_PRODUCAO.ordenarCelular}>
            Ordenar trabalhos
          </SheetTitle>
        </SheetHeader>

        <div className="flex flex-col gap-2 p-4">
          {[0, 1, 2].map((nivel) => (
            <div key={nivel} className="flex items-center gap-2">
              <span className="w-6 shrink-0 text-xs font-semibold text-zinc-400 dark:text-muted-foreground">
                {SORT_LEVEL_LABELS[nivel]}
              </span>
              <select
                value={rascunhoCriterios[nivel]}
                onChange={(e) => trocarCampo(nivel, e.target.value as SortField)}
                className="h-11 min-w-0 flex-1 rounded-md border border-zinc-200 bg-white px-2.5 text-base text-zinc-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-background dark:text-foreground"
                aria-label={`${SORT_LEVEL_LABELS[nivel]} prioridade de ordenação`}
              >
                {SORT_FIELDS.map((f) => (
                  <option key={f} value={f}>
                    {SORT_FIELD_LABELS[f]}
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={rascunhoCriterios[nivel] === "none"}
                onClick={() => alternarDirecao(nivel)}
                aria-label={rascunhoDirecoes[nivel] === "asc" ? "Ordem crescente" : "Ordem decrescente"}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-zinc-200 text-zinc-600 transition-colors hover:bg-zinc-50 disabled:opacity-40 dark:border-border dark:text-muted-foreground dark:hover:bg-muted"
              >
                {rascunhoDirecoes[nivel] === "asc" ? (
                  <ArrowUp className="h-4 w-4" />
                ) : (
                  <ArrowDown className="h-4 w-4" />
                )}
              </button>
            </div>
          ))}
        </div>

        <div className="flex gap-2 border-t border-zinc-100 p-3 dark:border-border">
          <button
            type="button"
            onClick={() => {
              setRascunhoCriterios(["none", "none", "none"]);
              setRascunhoDirecoes(["asc", "asc", "asc"]);
            }}
            className="h-11 flex-1 rounded-md border border-zinc-200 text-sm font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted"
          >
            Limpar
          </button>
          <button
            type="button"
            onClick={() => {
              onAplicar(rascunhoCriterios, rascunhoDirecoes);
              onFechar();
            }}
            className="h-11 flex-[1.4] rounded-md bg-brand-600 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-700"
          >
            Aplicar
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
